import type { AuthTokens, LoginResponse } from './types';
import type { ApiClient } from './client';

export const authMethods = {
async login(username: string, password: string): Promise<LoginResponse> {
      try {
        const response = await this.makeRequest<LoginResponse>('/login.php', {
          method: 'POST',
          body: JSON.stringify({
            username: this.sanitizeString(username),
            password: password // Don't sanitize password as it may contain special chars
          }),
        });

        if (response.success && response.data) {
          // Store authentication tokens with user data
          const tokens: AuthTokens = {
            accessToken: response.data.token,
            expiresAt: response.data.expires_at,
            user: response.data.user
          };
          this.saveAuthTokens(tokens);
          return response.data;
        } else {
          throw new Error(response.error || 'Login failed');
        }
      } catch (error: any) {
        console.error('Login error:', error);
        throw new Error(error.message || 'Login failed. Please check your credentials.');
      }
    },


    async logout(): Promise<void> {
      try {
        if (this.authTokens) {
          await this.makeRequest('/auth/logout.php', {
            method: 'POST',
          });
        }
      } catch (error) {
        console.error('Logout request failed:', error);
      } finally {
        this.clearAuthTokens();
      }
    },


    async refreshToken(): Promise<AuthTokens | null> {
      try {
        const response = await this.makeRequest<{ token: string; expires_at: string }>('/auth/refresh.php', {
          method: 'POST',
        });

        if (response.success && response.data) {
          const tokens: AuthTokens = {
            accessToken: response.data.token,
            expiresAt: response.data.expires_at,
            user: this.authTokens?.user,
          };
          this.saveAuthTokens(tokens);
          return tokens;
        }
      } catch (error) {
        console.error('Token refresh failed:', error);
        this.clearAuthTokens();
      }
      return null;
    },


    async register(userData: {
      registrationCode: string;
      username: string;
      email: string;
      password: string;
      firstName: string;
      lastName: string;
      country: string;
      stateProvince: string;
      gender: string;
      birthDate: string;
      instagramUsername?: string;
      facebookUrl?: string;
      websiteUrl?: string;
      bio?: string;
    }): Promise<any> {
      const sanitizedData = {
        registration_code: this.sanitizeString(userData.registrationCode),
        username: this.sanitizeString(userData.username),
        email: this.sanitizeString(userData.email),
        password: userData.password, // Don't sanitize
        first_name: this.sanitizeString(userData.firstName),
        last_name: this.sanitizeString(userData.lastName),
        country: this.sanitizeString(userData.country),
        state_province: this.sanitizeString(userData.stateProvince),
        gender: this.sanitizeString(userData.gender),
        birth_date: this.sanitizeDate(userData.birthDate),
        instagram_username: this.sanitizeString(userData.instagramUsername || ''),
        facebook_url: this.sanitizeUrl(userData.facebookUrl || ''),
        website_url: this.sanitizeUrl(userData.websiteUrl || ''),
        bio: this.sanitizeString(userData.bio || ''),
      };

      try {
        const response = await this.makeRequest('/auth/register.php', {
          method: 'POST',
          body: JSON.stringify(sanitizedData),
        });

        if (!response.success) {
          throw new Error(response.error || 'Registration failed');
        }

        return response;
      } catch (error: any) {
        console.error('Registration failed:', error);
        throw new Error(error.message || 'An error occurred during registration.');
      }
    }
} satisfies ThisType<ApiClient>;
